import { create } from 'zustand';
import apiClient from '../services/api';
import type { Submission } from '../types';
import type { PagedData, ListQueryRequest } from '../types';

interface SubmissionState {
  // 状态
  submissions: Submission[];
  currentSubmission: Submission | null;
  total: number;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchSubmissions: (params: ListQueryRequest) => Promise<void>;
  fetchSubmissionById: (id: string) => Promise<void>;
  createSubmission: (data: Partial<Submission>) => Promise<void>;
  updateSubmission: (id: string, data: Partial<Submission>) => Promise<void>;
  submitSubmission: (id: string) => Promise<void>;
  approveSubmission: (id: string, comment?: string) => Promise<void>;
  rejectSubmission: (id: string, reason: string) => Promise<void>;
  deleteSubmission: (id: string) => Promise<void>;
  setCurrentSubmission: (submission: Submission | null) => void;
}

export const useSubmissionStore = create<SubmissionState>((set, get) => ({
  submissions: [],
  currentSubmission: null,
  total: 0,
  isLoading: false,
  error: null,

  fetchSubmissions: async (params: ListQueryRequest) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.get('/submissions', { params });
      const data = response.data?.data || { items: [], total: 0 };
      set({ 
        submissions: data.items || [], 
        total: data.total || 0,
        isLoading: false 
      });
    } catch (error: any) {
      set({ 
        error: error.message || '获取上报列表失败', 
        isLoading: false,
        submissions: [],
        total: 0
      });
    }
  },

  fetchSubmissionById: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.get(`/submissions/${id}`);
      const data = response.data?.data;
      set({ currentSubmission: data, isLoading: false });
    } catch (error: any) {
      set({ 
        error: error.message || '获取上报详情失败', 
        isLoading: false 
      });
    }
  },

  createSubmission: async (data: Partial<Submission>) => {
    set({ isLoading: true, error: null });
    try {
      await apiClient.post('/submissions', data);
      set({ isLoading: false });
    } catch (error: any) {
      set({ 
        error: error.message || '创建上报失败', 
        isLoading: false 
      });
      throw error;
    }
  },

  updateSubmission: async (id: string, data: Partial<Submission>) => {
    set({ isLoading: true, error: null });
    try {
      await apiClient.put(`/submissions/${id}`, data);
      set({ isLoading: false });
    } catch (error: any) {
      set({ 
        error: error.message || '更新上报失败', 
        isLoading: false 
      });
      throw error;
    }
  },

  submitSubmission: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await apiClient.post(`/submissions/${id}/submit`);
      set({ isLoading: false });
    } catch (error: any) {
      set({ 
        error: error.message || '提交上报失败', 
        isLoading: false 
      });
      throw error;
    }
  },

  approveSubmission: async (id: string, comment?: string) => {
    set({ isLoading: true, error: null });
    try {
      await apiClient.post(`/submissions/${id}/approve`, { comment });
      set({ isLoading: false });
    } catch (error: any) {
      set({ 
        error: error.message || '审批上报失败', 
        isLoading: false 
      });
      throw error;
    }
  },

  rejectSubmission: async (id: string, reason: string) => {
    set({ isLoading: true, error: null });
    try {
      await apiClient.post(`/submissions/${id}/reject`, { reason });
      set({ isLoading: false });
    } catch (error: any) {
      set({ 
        error: error.message || '驳回上报失败', 
        isLoading: false 
      });
      throw error;
    }
  },

  deleteSubmission: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await apiClient.delete(`/submissions/${id}`);
      set({ isLoading: false });
    } catch (error: any) {
      set({ 
        error: error.message || '删除上报失败', 
        isLoading: false 
      });
      throw error;
    }
  },

  setCurrentSubmission: (submission: Submission | null) => {
    set({ currentSubmission: submission });
  },
}));
